import { useState } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { InviteUserModal } from './InviteUserModal';
import { 
  UserPlus, 
  Mail, 
  Clock, 
  CheckCircle, 
  XCircle, 
  RefreshCw,
  Trash2,
  Eye,
  Send,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired' | 'declined';
  invited_by: string;
  invited_by_name?: string;
  token: string;
  expires_at: string;
  created_at: string;
  module_access?: any[];
}

export function WorkspaceUserInvitations() {
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null);
  const [resendingInvitationId, setResendingInvitationId] = useState<string | null>(null);

  // Fetch invitations
  const { data: invitations = [], isLoading, refetch } = useQuery({
    queryKey: ['workspace-invitations', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];

      // Fetch invitations first
      const { data: invites, error } = await supabase
        .from('workspace_invitations')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch inviter profiles separately to avoid FK join issues
      const inviterIds = [...new Set((invites || []).map((inv: any) => inv.invited_by).filter(Boolean))];
      
      let inviterProfiles: Record<string, any> = {};
      if (inviterIds.length > 0) {
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, email, full_name')
          .in('id', inviterIds);
        
        if (profiles) {
          inviterProfiles = profiles.reduce((acc: Record<string, any>, profile: any) => {
            acc[profile.id] = profile;
            return acc;
          }, {});
        }
      }

      return (invites || []).map((inv: any) => {
        const inviter = inviterProfiles[inv.invited_by];
        return {
          ...inv,
          inviter,
          invited_by_name: inviter?.full_name || inviter?.email || 'Unknown',
        };
      });
    },
    enabled: !!currentWorkspace?.id,
  });

  // Resend invitation
  const resendMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      // Validate we have a single invitation ID
      if (!invitationId || Array.isArray(invitationId)) {
        throw new Error('Invalid invitation ID');
      }

      // Set loading state for this specific invitation
      setResendingInvitationId(invitationId);
      
      console.log('[WorkspaceUserInvitations] Resending invitation for ID:', invitationId);
      
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;
        
        if (!accessToken) {
          throw new Error('No active session. Please log in again.');
        }

        // Ensure we're sending a single invitation_id, not an array
        const payload = { invitation_id: invitationId };
        console.log('[WorkspaceUserInvitations] Sending resend request with payload:', payload);

        const { data, error } = await supabase.functions.invoke('workspace-resend-invitation', {
          body: payload,
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        console.log('[WorkspaceUserInvitations] Resend response:', { data, error });

        if (error) {
          // Handle structured error responses
          if (error.code || (error as any)?.error?.code) {
            throw error;
          }
          throw new Error(error.message || 'Failed to resend invitation');
        }

        // Check for error in response data
        if (data?.success === false && data.error) {
          throw data.error;
        }

        return { ...data, invitationId };
      } catch (err) {
        console.error('[WorkspaceUserInvitations] Error resending invitation:', err);
        throw err;
      } finally {
        // Clear loading state after request completes
        setResendingInvitationId(null);
      }
    },
    onSuccess: (data) => {
      const email = data?.email || 'the user';
      toast({
        title: 'Invitation Resent',
        description: data?.message || `Invitation email has been resent to ${email}`,
      });
      refetch();
    },
    onError: (error: any) => {
      const errorMessage = error?.message || error?.error?.message || 'Failed to resend invitation';
      const errorCode = error?.code || error?.error?.code;

      toast({
        title: errorCode === 'INVITATION_EXPIRED' ? 'Invitation Expired' : 'Error',
        description: errorMessage,
        variant: errorCode === 'INVITATION_EXPIRED' ? 'warning' : 'destructive',
      });
    },
  });

  // Cancel invitation
  const cancelMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const { data, error } = await supabase
        .from('workspace_invitations')
        .update({ status: 'declined' })
        .eq('id', invitationId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Invitation Cancelled',
        description: 'The invitation has been cancelled',
      });
      queryClient.invalidateQueries({ queryKey: ['workspace-invitations', currentWorkspace?.id] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to cancel invitation',
        variant: 'destructive',
      });
    },
  });

  const getStatusBadge = (status: string, expiresAt: string) => {
    const isExpired = new Date(expiresAt) < new Date();
    
    if (isExpired && status === 'pending') {
      return <Badge variant="destructive">Expired</Badge>;
    }

    switch (status) {
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'accepted':
        return <Badge variant="default" className="bg-green-500">Accepted</Badge>;
      case 'expired':
        return <Badge variant="destructive">Expired</Badge>;
      case 'declined':
        return <Badge variant="outline">Declined</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getRoleBadge = (role: string) => {
    const colors: Record<string, string> = {
      admin: 'destructive',
      manager: 'default',
      analyst: 'secondary',
      viewer: 'outline',
    };
    return <Badge variant={colors[role] as any}>{role}</Badge>;
  };

  const pendingCount = invitations.filter(i => i.status === 'pending' && new Date(i.expires_at) >= new Date()).length;
  const acceptedCount = invitations.filter(i => i.status === 'accepted').length;
  const expiredCount = invitations.filter(i => i.status === 'expired' || (i.status === 'pending' && new Date(i.expires_at) < new Date())).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">User Invitations</h2>
          <p className="text-muted-foreground">
            Manage user invitations and module access for {currentWorkspace?.name}
          </p>
        </div>
        <Button onClick={() => setShowInviteModal(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Invite User
        </Button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Invitations</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingCount}</div>
            <p className="text-xs text-muted-foreground">
              Awaiting user acceptance
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Accepted</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{acceptedCount}</div>
            <p className="text-xs text-muted-foreground">
              Users joined workspace
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expired</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{expiredCount}</div>
            <p className="text-xs text-muted-foreground">
              Invitations expired
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Invitations Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Invitations</CardTitle>
              <CardDescription>
                View and manage all workspace invitations. Use "Resend" to send invitations again. 
                Expired invitations will automatically get a new link when resent.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading invitations...</div>
          ) : invitations.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No invitations yet</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setShowInviteModal(true)}
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Send First Invitation
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Invited By</TableHead>
                    <TableHead>Expires At</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invitations.map((invitation) => (
                    <TableRow key={invitation.id}>
                      <TableCell className="font-medium">{invitation.email}</TableCell>
                      <TableCell>{getRoleBadge(invitation.role)}</TableCell>
                      <TableCell>{getStatusBadge(invitation.status, invitation.expires_at)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {invitation.invited_by_name}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex flex-col">
                          <span>{format(new Date(invitation.expires_at), 'MMM dd, yyyy')}</span>
                          {new Date(invitation.expires_at) < new Date() && (
                            <span className="text-xs text-destructive mt-0.5">(Expired)</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(invitation.created_at), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* All pending invitations (expired or not) - can resend until accepted */}
                          {(invitation.status === 'pending' || invitation.status === 'sent' || invitation.status === 'expired') && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent any row click events
                                  resendMutation.mutate(invitation.id);
                                }}
                                disabled={resendMutation.isPending || resendingInvitationId === invitation.id}
                                title={
                                  new Date(invitation.expires_at) < new Date()
                                    ? "This invitation has expired. Clicking 'Resend' will create a new invitation with a fresh link and extended expiry date."
                                    : `Resend the invitation email to ${invitation.email}. The same invitation link will be sent.`
                                }
                              >
                                {resendingInvitationId === invitation.id ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    Sending...
                                  </>
                                ) : (
                                  <>
                                    <Send className="h-4 w-4 mr-1" />
                                    {new Date(invitation.expires_at) < new Date() ? 'Resend (New Link)' : 'Resend'}
                                  </>
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedInvitation(invitation)}
                                title="View details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {/* Only show cancel for non-expired pending invitations */}
                              {invitation.status === 'pending' && new Date(invitation.expires_at) >= new Date() && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => cancelMutation.mutate(invitation.id)}
                                  disabled={cancelMutation.isPending}
                                  title="Cancel invitation"
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </>
                          )}
                          {/* Accepted/Declined - view only */}
                          {(invitation.status === 'accepted' || invitation.status === 'declined') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedInvitation(invitation)}
                              title="View details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invite User Modal */}
      <InviteUserModal open={showInviteModal} onOpenChange={setShowInviteModal} />

      {/* Invitation Detail Dialog */}
      <Dialog open={!!selectedInvitation} onOpenChange={() => setSelectedInvitation(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invitation Details</DialogTitle>
            <DialogDescription>
              View detailed information about this invitation
            </DialogDescription>
          </DialogHeader>
          {selectedInvitation && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Email</label>
                <p className="text-sm text-muted-foreground">{selectedInvitation.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium">Role</label>
                <div className="mt-1">{getRoleBadge(selectedInvitation.role)}</div>
              </div>
              <div>
                <label className="text-sm font-medium">Status</label>
                <div className="mt-1">
                  {getStatusBadge(selectedInvitation.status, selectedInvitation.expires_at)}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Module Access</label>
                <div className="mt-2 space-y-1">
                  {selectedInvitation.module_access && selectedInvitation.module_access.length > 0 ? (
                    selectedInvitation.module_access.map((module: any) => (
                      <Badge key={module.module_id} variant="outline" className="mr-2">
                        {module.module_id}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">Default access only</p>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Invitation Link</label>
                <p className="text-xs text-muted-foreground break-all mt-1">
                  {window.location.origin}/invite/{selectedInvitation.id}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

